import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import ShieldOutlined from '@mui/icons-material/ShieldOutlined';
import { Autocomplete, Box, IconButton, InputAdornment, Paper, TextField, Typography } from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Context } from '../../service/Context';
import VirtualizedListbox from '../../utils/UtilesBuscadores';
import { capitalizar, ordenarPorCategoria } from '../../utils/UtilesComun';
import { PaperApp } from '../custom/CustomComponents';

/**
 * Pestaña de búsqueda de clubes para la pantalla unificada de exploración y búsqueda.
 */
const BuscadorClubTab = () => {
  const navigate = useNavigate();
  const { idUsuario, obtenerTodosClubes, obtenerEquiposClub, todosClubesCache } = useContext(Context);
  const [todosClubes, setTodosClubes] = useState(todosClubesCache || []);
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    async function cargarDatos() {
      if (idUsuario) {
        if (!todosClubesCache || todosClubesCache.length === 0) {
          const clubes = await obtenerTodosClubes();
          setTodosClubes(clubes || []);
        }
      }
    }
    cargarDatos();
  }, [idUsuario, todosClubesCache, obtenerTodosClubes]);

  const clickClub = async (club) => {
    if (club !== null) {
      try {
        const equipos = await obtenerEquiposClub(club.first);
        const equiposOrdenados = ordenarPorCategoria(equipos);
        if (equiposOrdenados.length > 0 && equiposOrdenados[0]?.idEquipo) {
          navigate(`/equipo/${equiposOrdenados[0].idEquipo}`);
          return;
        }
      } catch (error) {
        console.error('Error al obtener equipos del club:', error);
      }
      navigate(`/club/${club.first}`);
    }
  };

  const normalizeStr = (str) =>
    (str || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');

  const customFilterOptions = (options, { inputValue: searchVal }) => {
    if (!searchVal) {
      return options;
    }

    const inputTerms = normalizeStr(searchVal)
      .split(' ')
      .filter((term) => term.length > 0);

    return options.filter((option) => {
      const normalizedLabel = normalizeStr(option.second);
      return inputTerms.every((term) => normalizedLabel.includes(term));
    });
  };

  return (
    <Box>
      {/* Cabecera de la sección */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
          mb: 2.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: '10px',
              backgroundColor: 'rgba(15, 118, 110, 0.08)',
              color: '#0f766e',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid rgba(15, 118, 110, 0.20)',
            }}
          >
            <ShieldOutlined sx={{ fontSize: 24 }} />
          </Box>

          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '1.25rem', sm: '1.45rem' },
                  color: 'text.primary',
                  lineHeight: 1.2,
                }}
              >
                Buscar Clubes
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Tarjeta principal con el buscador */}
      <PaperApp sx={{ p: { xs: 2.5, sm: 4 }, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Box sx={{ width: '100%', maxWidth: 640 }}>
          <Typography sx={{ fontSize: '0.86rem', fontWeight: 600, color: '#334155', mb: 1 }}>
            Introduce el nombre del club que deseas consultar:
          </Typography>

          <Autocomplete
            id="buscador-clubes-tab"
            fullWidth
            filterOptions={customFilterOptions}
            options={todosClubes}
            ListboxComponent={VirtualizedListbox}
            getOptionLabel={(option) => capitalizar(option.second || '')}
            onChange={(event, value) => clickClub(value)}
            inputValue={inputValue}
            onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
            clearOnBlur={false}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder=""
                variant="outlined"
                size="medium"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#0f766e', fontSize: '1.35rem', ml: 0.5 }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <>
                      {inputValue ? (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setInputValue('')} sx={{ p: 0.25, mr: 0.5 }}>
                            <ClearIcon sx={{ fontSize: '1.05rem', color: '#94a3b8' }} />
                          </IconButton>
                        </InputAdornment>
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                  sx: {
                    borderRadius: '10px',
                    backgroundColor: '#f8fafc',
                    fontSize: '0.90rem',
                    fontWeight: 500,
                    '& fieldset': { borderColor: '#cbd5e1' },
                    '&:hover fieldset': { borderColor: '#0f766e' },
                    '&.Mui-focused': {
                      backgroundColor: '#ffffff',
                      '& fieldset': { borderColor: '#0f766e', borderWidth: 2 },
                    },
                  },
                }}
              />
            )}
            renderOption={(props, option) => [
              props,
              <Box key={option.first} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5 }}>
                <ShieldOutlined sx={{ fontSize: '1.1rem', color: '#0f766e', opacity: 0.8 }} />
                <Typography sx={{ fontSize: '0.86rem', fontWeight: 600, color: '#1e293b' }}>
                  {capitalizar(option.second)}
                </Typography>
              </Box>,
            ]}
            PaperComponent={(paperProps) => (
              <Paper
                {...paperProps}
                sx={{
                  mt: 1,
                  borderRadius: '10px',
                  border: '1px solid #cbd5e1',
                  boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.15)',
                  overflow: 'hidden',
                  backgroundColor: '#ffffff',
                }}
              />
            )}
          />

          <Typography sx={{ color: '#64748b', fontSize: '0.78rem', mt: 1.5, textAlign: 'center' }}>
            Al seleccionar un club, accederás automáticamente a la plantilla de su primer equipo disponible o a su
            ficha.
          </Typography>
        </Box>
      </PaperApp>
    </Box>
  );
};

export default BuscadorClubTab;
